<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      We know cardio training can be seen as hard work - or even boring - but incorporating it into
      your training programs is important for clients to receive a well-balanced plan-of-action.
    </p>
    <p class="mb-5">
      Below are some of the main factors you’ll need to consider when designing a cardio programme
      for a client.
    </p>
    <h4 class="mb-5">1. Frequency & Intensity</h4>
    <p class="mb-5">
      ‘Frequency’ is how often the client will perform an exercise, while ‘intensity’ refers to how
      hard they work while doing it.
    </p>
    <p class="mb-5">
      Lower intensity cardio workouts can be performed more frequently, even daily, while
      high-intensity ones will need some rest periods between sessions to avoid risk of injury.
    </p>
    <p class="mb-5">
      When working with beginner clients, cardio workouts begin at 1 to 3 days per week which then
      increases as their fitness levels improve.
    </p>

    <div class="vx-row my-5 flex items-center justify-center">
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <h4 class="mb-5">2. Time Spent Exercising</h4>

        <p class="mb-5">
          There are guidelines from the Australian government on the amount of exercise that is
          recommended for adults. They state that a person should,
        </p>
        <p class="mb-5">
          “Accumulate 150 to 300 minutes (2 ½ to 5 hours) of moderate-intensity physical activity or
          75 to 150 minutes (1 ¼ to 2 ½ hours) of vigorous-intensity physical activity, or an
          equivalent combination of both moderate and vigorous activities, each week.”<br />
          (Physical activity and sedentary behaviour guidelines - adults (fact sheet) May 2021)
        </p>
        <p class="mb-5">
          Of course, a lot will depend on client schedules, but PTs usually recommend some regular
          exercise outside of sessions in order to meet these needs.
        </p>
        <p class="mb-5">
          Some clients might struggle to find the time or even motivation outside of their training
          sessions, that’s why it’s important to make sure you build cardio into their training
          programme.
        </p>
        <h4 class="mb-5">3. Types of cardio</h4>
        <p class="mb-5">
          Cardio exercise can take different forms, and the type of exercise you choose will depend
          on a variety of factors, including a client’s goals, access to equipment and time
          available.
        </p>
        <p class="mb-5">
          For example, if a client’s goal is to run a marathon, both running on a treadmill and a
          track will fit their needs - so either would be appropriate.
        </p>
        <h4 class="mb-5">4. Make It Fun!</h4>
        <p class="mb-5">
          As a trainer, although you’ll want to get your client to reach their goals as soon as
          possible, you’ll have to be skillful in managing their motivation levels.
        </p>
        <p class="mb-5">
          As you’re running a business, you’ll want clients to return to you time and again.
        </p>
        <p class="mb-5">
          An effective way of gaining loyal clients is by making sure they enjoy sessions.
        </p>
        <p class="mb-5">
          That doesn’t mean they don’t work hard - it means you should encourage your clients to
          have an input, taking their likes and interests into account.
        </p>
        <p class="mb-5">
          When clients have fun and make progress, they are much more likely to keep showing up!
        </p>
      </div>
      <div class="vx-col xl:w-1/2 lg:w-1/2 w-full my-5">
        <img
          src="@/assets/images/experiences/fitness/cardio_sidebar.jpg"
          alt=""
          class="mx-auto mb-5 img-responsive"
        />
      </div>
    </div>

    <h4 class="mb-5">5) HIIT: High-Intensity Interval Training</h4>
    <p class="mb-5">
      High-Intensity Interval Training, or HIIT, is a great way to get maximum results in the
      minimum time. To find out more about how HIIT works, watch the following short video:
    </p>
    <responsive-youtube-wrapper :image="videos[1].image" :video="videos[1].url" />
    <p class="mb-5">Now it’s time to design your own HIIT exercise routine.</p>
    <p class="mb-5">
      First you’ll need to choose some suitable cardio-type exercises to incorporate into your HIIT
      routine.
    </p>
    <p class="mb-5">
      Watch the video below to get some ideas for exercises to include in your HIIT routine!
    </p>
    <responsive-youtube-wrapper :image="videos[2].image" :video="videos[2].url" />
    <p class="mb-5">Now, list 7 exercises you like that you could use in a HIIT routine.</p>
    <div v-for="index in 7" :key="index" class="overflow-hidden">
      <p class="float-left" style="padding: 8px 5px 0; width: 20px" v-if="isEditable">
        {{ index }}:
      </p>
      <p class="float-left py-5 pr-2" v-else>{{ index }}:</p>
      <text-block v-model="data['question' + index]" :editable="isEditable" />
    </div>

    <p class="mb-5">
      Take a look at the image explaining the steps to designing your own HIIT workout. Then,
      complete the table below.
    </p>

    <centered-image-wrapper
      :image="image1"
      classes="2/3"
      alt="Your Perfect HIIT Formula infographic. 1. Choose your intensity: give it your all but don't give up on form; aim for 80-95% of your max heart rate for the intense intervals. 2. Pick your total time: a solid HIIT workout takes anywhere from 5 to 30 minutes. 3. Determine your work-to-rest ratio; for beginners, week 1: 30 seconds work / 60 seconds rest; week 2: 30 / 45; week 3: 30 / 30; week 4: 45 / 30. Short on time? Opt for 20 seconds work and 10 seconds rest, and use a timer with alerts. 4. Select your exercises: any plyometric move, such as standing mountain climbers, push-ups, speed squats, 4-point plankers, burpees, high plank punches, sprints, or Y-W-T holds. 5. Don't forget a warm-up and cool down: start with a few dynamic stretches and high jumps; cool down by static stretching each major muscle group. Source: Daily Burn Life."
    />
    <table class="table default-table">
      <tr class="header">
        <th colspan="3">MY HIIT ROUTINE</th>
      </tr>
      <tr class="subheader">
        <th scope="col" colspan="2" width="40%">STEP</th>
        <th scope="col" width="60%">DETAILS</th>
      </tr>
      <tr class="body">
        <th scope="row" class="subheader">1.</th>
        <td>Warm-Up</td>
        <td>
          <div class="overflow-hidden">
            <p class="float-left py-2 px-2">Length of Time:</p>
            <text-block
              v-model="data['question8']"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2">minutes</p>
          </div>
          <br />
          <div class="overflow-hidden">
            <p class="float-left py-2 px-2">Exercise:</p>
            <text-block
              v-model="data['question9']"
              :editable="isEditable"
              style="width: 150px; float: left"
              pclass="my-2"
            />
          </div>
        </td>
      </tr>
      <tr>
        <th scope="row" class="subheader" width="5%">2.</th>
        <td>Target Intensity</td>
        <td>
          <div class="overflow-hidden">
            <text-block
              v-model="data['question10']"
              :editable="isEditable"
              style="width: 50px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2">%</p>
          </div>
        </td>
      </tr>
      <tr>
        <th scope="row" class="subheader">3.</th>
        <td>Total Time</td>
        <td>
          <div class="overflow-hidden">
            <text-block
              v-model="data['question11']"
              :editable="isEditable"
              style="width: 50px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2">minutes</p>
          </div>
        </td>
      </tr>
      <tr>
        <th scope="row" class="subheader">4.</th>
        <td>Work-to-Rest Ratio</td>
        <td>
          <div class="overflow-hidden">
            <text-block
              v-model="data['question12']"
              :editable="isEditable"
              style="width: 50px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2">sec work /</p>
            <text-block
              v-model="data['question13']"
              :editable="isEditable"
              style="width: 50px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2">sec rest</p>
          </div>
        </td>
      </tr>
      <tr>
        <th scope="row" class="subheader">5.</th>
        <td>Select Exercises</td>
        <td>
          <div class="overflow-hidden">
            <text-block
              v-model="data['question14']"
              :editable="isEditable"
              style="width: 200px; float: left"
              pclass="my-2"
            />
          </div>
        </td>
      </tr>
      <tr>
        <th scope="row" class="subheader">6.</th>
        <td>Cool Down</td>
        <td>
          <div class="overflow-hidden">
            <p class="float-left py-2 px-2">Length of Time:</p>
            <text-block
              v-model="data['question15']"
              :editable="isEditable"
              style="width: 100px; float: left"
              pclass="my-2"
            />
            <p class="float-left py-2 px-2">minutes</p>
          </div>
          <br />
          <div class="overflow-hidden">
            <p class="float-left py-2 px-2">Exercise:</p>
            <text-block
              v-model="data['question16']"
              :editable="isEditable"
              style="width: 150px; float: left"
              pclass="my-2"
            />
          </div>
        </td>
      </tr>
    </table>
    <p class="mb-5">
      Now, you have your HIIT routine plan, can you walk the talk? Set your alarm and perform your
      HIIT routine.
    </p>
    <p class="mb-5">You now have two options to show yourself performing your HIIT routine:</p>
    <p class="mb-5">
      1. Record a video of yourself performing one round of your HIIT routine (i.e. 30 seconds to 45
      seconds) and upload that video here
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Video'"
      index="1"
      filetype="media"
    />

    <p class="mb-5 text-bold text-underline">OR</p>
    <p class="mb-5">
      2. Get someone to take a photo of you performing your routine and upload it here.
    </p>

    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_2']"
      :is-editable="isEditable"
      :upload-btn-text="'Upload Photo'"
      index="2"
      filetype="media"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/fitness/hiit_splash.jpg'),
          url: 'https://www.youtube.com/embed/2GgKm_54kXI',
        },
        2: {
          image: require('@/assets/images/experiences/fitness/99_exercises_splash.jpg'),
          url: 'https://www.youtube.com/embed/2EO_Z8cxMSE',
        },
      },
      image1: require('@/assets/images/experiences/fitness/HIIT-Formula-Infographic-1.jpg'),
      uploaded_files: {
        uploaded_file_1: '',
        uploaded_file_2: '',
      },
    };
  },
  methods: methods,
  computed: computed,
  inject: inject,
  mounted() {
    if (typeof this.uploaded_files !== 'undefined') {
      for (let i = 1; i <= Object.keys(this.uploaded_files).length; i++) {
        if (typeof this.data['uploaded_file_' + i] !== 'undefined') {
          this.uploaded_files['uploaded_file_' + i] = this.data['uploaded_file_' + i];
        }
      }
    }
  },
};
</script>
